import { useState, useEffect } from 'react';
import { lsGet, lsSet } from '../lib/storage';
import { trackEvent } from '../lib/analytics';

const DISMISSED_KEY   = 'diwanPWADismissed';
const LAST_SHOWN_KEY  = 'diwanPWALastShown';
const DISMISS_CNT_KEY = 'diwanPWADismissCount';

const REPEAT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_AUTO_DISMISSALS = 3; // stop auto-showing after 3 "Not now" taps

const JOSEFIN = "'Josefin Sans', sans-serif";
const NASKH   = "'Noto Naskh Arabic', serif";

function isInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// Show on first visit, then every 7 days — unless permanently dismissed
// (installed, "Done", or "Not now" tapped MAX_AUTO_DISMISSALS times).
function shouldAutoShow() {
  if (isInstalled()) return false;
  if (lsGet(DISMISSED_KEY) === 'true') return false;
  const dismissCount = parseInt(lsGet(DISMISS_CNT_KEY) ?? '0', 10);
  if (dismissCount >= MAX_AUTO_DISMISSALS) return false;
  const lastShown = parseInt(lsGet(LAST_SHOWN_KEY) ?? '0', 10);
  return Date.now() - lastShown >= REPEAT_INTERVAL_MS;
}

function Step({ number, children }) {
  return (
    <div style={{
      display: 'flex',
      gap: '0.85rem',
      alignItems: 'flex-start',
      padding: '0.7rem 0',
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{
        width: 22, height: 22,
        borderRadius: '50%',
        background: 'var(--em-soft)',
        border: '1px solid var(--em-line)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontFamily: JOSEFIN,
        fontSize: '0.6rem',
        letterSpacing: '0.05em',
        color: 'var(--em)',
        marginTop: 2,
      }}>
        {number}
      </div>
      <div style={{
        fontSize: '0.85rem',
        color: 'var(--text2)',
        lineHeight: 1.55,
        flex: 1,
      }}>
        {children}
      </div>
    </div>
  );
}

function IOSInstructions() {
  return (
    <div>
      <Step number="1">
        Open <strong style={{ color: 'var(--text)' }}>aldiwan.uk</strong> in{' '}
        <strong style={{ color: 'var(--text)' }}>Safari</strong>. The install
        option is only available in Safari on iPhone and iPad — not in Chrome
        or other browsers.
      </Step>
      <Step number="2">
        Tap the{' '}
        <strong style={{ color: 'var(--text)' }}>Share button</strong>
        {' '}— the square with an arrow pointing upward at the bottom of
        the Safari toolbar.
      </Step>
      <Step number="3">
        Scroll down the share sheet and tap{' '}
        <strong style={{ color: 'var(--text)' }}>Add to Home Screen</strong>.
      </Step>
      <Step number="4">
        Tap{' '}
        <strong style={{ color: 'var(--text)' }}>Add</strong> in the top-right
        corner. Al-Diwan will appear on your Home Screen like a native app —
        faster, fullscreen, no browser bar.
      </Step>
    </div>
  );
}

function AndroidInstructions() {
  return (
    <div>
      <Step number="1">
        Open <strong style={{ color: 'var(--text)' }}>aldiwan.uk</strong> in{' '}
        <strong style={{ color: 'var(--text)' }}>Chrome</strong>.
      </Step>
      <Step number="2">
        Tap the{' '}
        <strong style={{ color: 'var(--text)' }}>three-dot menu</strong>
        {' '}⋮ in the top-right corner of the browser.
      </Step>
      <Step number="3">
        Tap{' '}
        <strong style={{ color: 'var(--text)' }}>Add to Home screen</strong>.
      </Step>
      <Step number="4">
        Tap{' '}
        <strong style={{ color: 'var(--text)' }}>Add</strong> on the
        confirmation prompt. Al-Diwan will appear on your Home Screen and
        launch faster than the in-browser version.
      </Step>
    </div>
  );
}

function detectPlatform() {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'unknown';
}

export default function PWAInstallSheet({ forceOpen = false, onClose }) {
  const [open, setOpen]           = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const platform = detectPlatform();

  useEffect(() => {
    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferredPrompt(e);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  // Auto-show on first visit and every 7 days — forceOpen bypasses (hint button path).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (forceOpen) { setOpen(true); return; }
    if (!shouldAutoShow()) return;
    const t = setTimeout(() => {
      setOpen(true);
      lsSet(LAST_SHOWN_KEY, String(Date.now()));
      trackEvent('pwa_prompt_shown', { trigger: 'auto' });
    }, 1500);
    return () => clearTimeout(t);
  }, [forceOpen]);

  function close() {
    // "Not now" — count the dismissal; permanently stop after MAX_AUTO_DISMISSALS
    const count = parseInt(lsGet(DISMISS_CNT_KEY) ?? '0', 10) + 1;
    lsSet(DISMISS_CNT_KEY, String(count));
    if (count >= MAX_AUTO_DISMISSALS) lsSet(DISMISSED_KEY, 'true');
    setOpen(false);
    onClose?.();
  }

  function handleDone() {
    // "Done" = user installed (or confirmed) — permanently stop auto-showing
    lsSet(DISMISSED_KEY, 'true');
    trackEvent('pwa_install_done');
    setOpen(false);
    onClose?.();
  }

  async function handleNativeInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') lsSet(DISMISSED_KEY, 'true');
    trackEvent('pwa_install_done', { outcome });
    setOpen(false);
    onClose?.();
  }

  if (!open) return null;

  return (
    <>
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1200,
          animation: 'fadeIn 0.2s ease',
        }}
      />

      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'var(--bg2)',
        borderTop: '1px solid var(--border)',
        borderRadius: '16px 16px 0 0',
        zIndex: 1201,
        animation: 'slideUp 0.3s ease',
        maxHeight: '88svh',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Handle */}
        <div style={{
          width: 36, height: 4,
          borderRadius: 2,
          background: 'var(--border)',
          margin: '12px auto 0',
          flexShrink: 0,
        }} />

        {/* Header */}
        <div style={{ padding: '1rem 1.5rem 0.5rem', flexShrink: 0 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.25rem',
          }}>
            <div style={{
              fontFamily: JOSEFIN,
              fontSize: '0.58rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'var(--text3)',
            }}>
              Add to Home Screen
            </div>
            <button
              onClick={close}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text3)',
                fontSize: '1.1rem',
                cursor: 'pointer',
                lineHeight: 1,
                padding: '0 0.25rem',
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div style={{
            fontFamily: NASKH,
            fontSize: '1.1rem',
            color: 'var(--text)',
            marginBottom: '0.2rem',
          }}>
            الديوان — على شاشتك
          </div>
          <div style={{
            fontSize: '0.8rem',
            color: 'var(--text3)',
            lineHeight: 1.5,
          }}>
            Install Al-Diwan for faster loading, offline reading, and a
            fullscreen experience without the browser bar.
          </div>
        </div>

        {/* Platform label */}
        <div style={{
          margin: '0.5rem 1.5rem 0',
          padding: '0.4rem 0.75rem',
          background: 'var(--bg3)',
          borderRadius: 6,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.9rem' }}>
            {platform === 'ios' ? '🍎' : platform === 'android' ? '🤖' : '📱'}
          </span>
          <span style={{ fontFamily: JOSEFIN, fontSize: '0.55rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)' }}>
            {platform === 'ios' ? 'iPhone / iPad' : platform === 'android' ? 'Android' : 'Choose your device below'}
          </span>
        </div>

        {/* Scrollable instructions */}
        <div style={{ padding: '0.25rem 1.5rem 0.5rem', overflowY: 'auto', flex: 1 }}>
          {platform === 'ios'    && <IOSInstructions />}
          {platform === 'android' && <AndroidInstructions />}
          {platform === 'unknown' && <IOSInstructions />}
        </div>

        {/* Actions */}
        <div style={{
          padding: '0.85rem 1.5rem',
          paddingBottom: 'calc(0.85rem + env(safe-area-inset-bottom))',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          flexShrink: 0,
          background: 'var(--bg2)',
        }}>
          {deferredPrompt && platform === 'android' && (
            <button
              onClick={handleNativeInstall}
              style={{
                width: '100%',
                background: 'var(--em)',
                border: 'none',
                borderRadius: 6,
                padding: '0.8rem',
                fontFamily: JOSEFIN,
                fontSize: '0.6rem',
                letterSpacing: '0.13em',
                textTransform: 'uppercase',
                color: 'var(--bg)',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Install Now →
            </button>
          )}

          <button
            onClick={handleDone}
            style={{
              width: '100%',
              background: deferredPrompt && platform === 'android'
                ? 'transparent'
                : 'var(--em)',
              border: deferredPrompt && platform === 'android'
                ? '1px solid var(--border)'
                : 'none',
              borderRadius: 6,
              padding: '0.8rem',
              fontFamily: JOSEFIN,
              fontSize: '0.6rem',
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
              color: deferredPrompt && platform === 'android'
                ? 'var(--text3)'
                : 'var(--bg)',
              cursor: 'pointer',
            }}
          >
            Done ✓
          </button>

          <button
            onClick={close}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              padding: '0.4rem',
              fontFamily: JOSEFIN,
              fontSize: '0.55rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--text3)',
              cursor: 'pointer',
            }}
          >
            Not now
          </button>
        </div>
      </div>
    </>
  );
}
